import React, { useState, useEffect } from "react";
import {
  Upload,
  X,
  ChevronDown,
  ChevronRight,
  FileText,
  File,
  FileEdit,
  Plus,
  Users,
  Palette,
  Settings,
  Save,
  Edit3,
  Star,
  StarOff,
  FileText as VTNIcon,
} from "lucide-react";
import { useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Alert,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Paper,
  Modal,
  Box,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Divider,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Badge,
} from "@mui/material";
import { getToken, base_url } from "../config/config";
import VTNDocumentForm from "../components/VTNDocumentForm";
//
const DocumentLabeler = () => {
  const { anr } = useParams();
  const [categories, setCategories] = useState({});
  const [categoryIds, setCategoryIds] = useState({});
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newLabel, setNewLabel] = useState({
    name: "",
    personalCategory: false,
  });
  const [newSubCategoryName, setNewSubCategoryName] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [labels, setLabels] = useState([]); // New state for storing all labels
  const [favouriteLabels, setFavouriteLabels] = useState(new Set());
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [enableEmployeeSelect, setEnableEmployeeSelect] = useState(false);
  const [categoryType, setCategoryType] = useState("personal");

  // Enhanced subcategory management states
  const [subcategories, setSubcategories] = useState([]);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [subcategoryEmployees, setSubcategoryEmployees] = useState([]);
  const [subcategoryColor, setSubcategoryColor] = useState("#1976d2");
  const [showColorPalette, setShowColorPalette] = useState(false);

  // VTN Document states
  const [showVTNForm, setShowVTNForm] = useState(false);
  const [vtnDocuments, setVtnDocuments] = useState([]);
  const [editingVTN, setEditingVTN] = useState(null);
  const fileInputRef = React.useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Professional color palette for subcategories
  const colorPalette = [
    "#1976d2",
    "#388e3c",
    "#f57c00",
    "#d32f2f",
    "#7b1fa2",
    "#0097a7",
    "#5d4037",
    "#455a64",
    "#e64a19",
    "#c2185b",
    "#303f9f",
    "#689f38",
    "#fbc02d",
    "#f44336",
    "#9c27b0",
    "#00acc1",
    "#795548",
    "#607d8b",
    "#ff5722",
    "#e91e63",
  ];

  const SUPPORTED_IMAGE_EXTENSIONS = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "webp",
    "tiff",
    "svg",
    "ico",
    "heic",
    "heif",
    "avif",
    "apng",
  ];
  const token = getToken();

  useEffect(() => {
    fetchCategories();
    fetchAllLabels();
    fetchFavouriteLabels();
    fetchAllEmployees();
    fetchSubcategories();
    fetchVTNDocuments();
  }, []);

  const fetchFavouriteLabels = async () => {
    try {
      const res = await fetch(`${base_url}/v1/getFavouriteLabels`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      // data may be an array of names or objects - normalize to names
      const items = data?.data || data || [];
      const favSet = new Set(
        items.map((it) =>
          typeof it === "string"
            ? it
            : it.name || it.label || it.labelName || it.labelId || ""
        )
      );
      setFavouriteLabels(favSet);
    } catch (err) {
      console.error("Failed to load favourite labels:", err);
    }
  };

  const fetchAllLabels = async () => {
    try {
      const response = await fetch(`${base_url}/v1/getAllLabels`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        redirect: "follow",
      });
      const result = await response.json();
      setLabels(result);
    } catch (error) {
      console.error("Error fetching labels:", error);
      setError("Failed to fetch labels");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${base_url}/v1/getAllSubCategories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const organizedData = {};
      const idMapping = {};

      data.data.forEach((item) => {
        if (item.labelId) {
          const categoryName = item.labelId.name;
          if (!organizedData[categoryName]) {
            organizedData[categoryName] = [];
          }
          organizedData[categoryName].push(item.name);
        } else {
          organizedData[item._id] = item.name;
        }
        idMapping[item.name] = item._id;
      });

      setCategories(organizedData);
      console.log("Categories fetched:", organizedData);
      setCategoryIds(idMapping);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Failed to fetch categories");
    }
  };

  const fetchAllEmployees = async () => {
    try {
      const employeesResponse = await fetch(
        `${base_url}/v1/getAllEmployees?includeAdmins=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const employeesData = await employeesResponse.json();
      setEmployees(employeesData);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  // Fetch enhanced subcategories with employee and color data
  const fetchSubcategories = async () => {
    try {
      const response = await fetch(
        `${base_url}/v1/getAllSubCategoriesEnhanced`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      setSubcategories(data.data || []);
    } catch (error) {
      console.error("Error fetching enhanced subcategories:", error);
      // Fallback to existing categories if enhanced endpoint doesn't exist
      const existingCategories = Object.entries(categories).map(
        ([name, items]) => ({
          name,
          items: Array.isArray(items) ? items : [items],
          assignedEmployees: [],
          colorCode: "#1976d2",
        })
      );
      setSubcategories(existingCategories);
    }
  };

  const handleToggleFavourite = async (label) => {
    // label is a string name in this UI
    console.log("Toggling favourite for label:", label);
    const name = typeof label === "string" ? label : String(label);
    const currently = favouriteLabels.has(name);

    // Optimistic UI update
    setFavouriteLabels((prev) => {
      const next = new Set(prev);
      if (currently) next.delete(name);
      else next.add(name);
      return next;
    });

    try {
      const body = { name };
      if (currently) body.remove = true; // backend may honor remove flag

      const res = await fetch(`${base_url}/v1/addToFavourite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("Failed to toggle favourite");

      // refresh from server to ensure consistency
      await fetchFavouriteLabels();
    } catch (err) {
      console.error("Failed to toggle favourite:", err);
      // revert optimistic update on error
      setFavouriteLabels((prev) => {
        const next = new Set(prev);
        if (currently) next.add(name);
        else next.delete(name);
        return next;
      });
    }
  };

  const handleCreateLabel = async () => {
    try {
      const response = await fetch(`${base_url}/v1/createLabel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newLabel.name,
          personalCategory: newLabel.personalCategory,
        }),
      });

      if (!response.ok) throw new Error("Failed to create label");

      setSuccess("Label created successfully");
      setNewLabel({ name: "", personalCategory: false });
      fetchCategories();
    } catch (error) {
      setError("Failed to create label");
      console.error("Error creating label:", error);
    }
  };

  const handleCreateSubCategory = async () => {
    // Prepare request body with enhanced features
    const body = {
      name: newSubCategoryName,
      labelId: null,
      isPersonal: categoryType === "personal",
      colorCode: subcategoryColor, // Add color support
    };
    if (categoryType === "employee" && selectedEmployees.length > 0) {
      body.users = selectedEmployees.filter(
        (id) => typeof id === "string" && id !== "all"
      );
      body.assignedEmployees = selectedEmployees; // Enhanced employee assignment
    }

    try {
      const response = await fetch(`${base_url}/v1/createSubCategoryEnhanced`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // Fallback to original endpoint if enhanced doesn't exist
        const fallbackResponse = await fetch(
          `${base_url}/v1/createSubCategory`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              name: newSubCategoryName,
              labelId: null,
              isPersonal: categoryType === "personal",
              users:
                categoryType === "employee"
                  ? selectedEmployees.filter(
                      (id) => typeof id === "string" && id !== "all"
                    )
                  : undefined,
            }),
          }
        );

        if (!fallbackResponse.ok)
          throw new Error("Failed to create subcategory");
      }

      setSuccess("Kategorie erfolgreich erstellt");
      setNewSubCategoryName("");
      setSelectedLabel("");
      setSelectedEmployees([]);
      setSubcategoryColor("#1976d2");
      fetchCategories();
      fetchSubcategories();
    } catch (error) {
      setError("Fehler beim Erstellen der Kategorie");
      console.error("Error creating subcategory:", error);
    }
  };

  // Handle subcategory enhancement (assign employees and colors to existing subcategories)
  const handleEnhanceSubcategory = async (subcategory) => {
    setSelectedSubcategory(subcategory);
    setSubcategoryEmployees(subcategory.assignedEmployees || []);
    setSubcategoryColor(subcategory.colorCode || "#1976d2");
    setShowSubcategoryModal(true);
  };

  // Save enhanced subcategory data
  const handleSaveSubcategoryEnhancements = async () => {
    if (!selectedSubcategory) return;

    try {
      const response = await fetch(`${base_url}/v1/updateSubCategoryEnhanced`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subcategoryId: selectedSubcategory._id || selectedSubcategory.id,
          assignedEmployees: subcategoryEmployees,
          colorCode: subcategoryColor,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update subcategory");
      }

      setSuccess("Kategorie erfolgreich aktualisiert");
      setShowSubcategoryModal(false);
      setSelectedSubcategory(null);
      fetchSubcategories();
    } catch (error) {
      setError("Fehler beim Aktualisieren der Kategorie");
      console.error("Error updating subcategory:", error);
    }
  };

  // VTN Document functions
  const fetchVTNDocuments = async () => {
    try {
      const response = await fetch(`${base_url}/v1/getVTNDocuments/${anr}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setVtnDocuments(data.data || []);
    } catch (error) {
      console.error("Error fetching VTN documents:", error);
    }
  };

  const handleVTNSave = (result) => {
    setSuccess("VTN Dokument erfolgreich gespeichert!");
    fetchVTNDocuments(); // Refresh the VTN documents list
  };

  const handleEditVTN = (vtnDoc) => {
    setEditingVTN(vtnDoc);
    setShowVTNForm(true);
  };

  const handleDeleteVTN = async (vtnId) => {
    if (
      window.confirm(
        "Sind Sie sicher, dass Sie dieses VTN Dokument löschen möchten?"
      )
    ) {
      try {
        const response = await fetch(
          `${base_url}/v1/deleteVTNDocument/${vtnId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.ok) {
          setSuccess("VTN Dokument erfolgreich gelöscht");
          fetchVTNDocuments();
        }
      } catch (error) {
        setError("Fehler beim Löschen des VTN Dokuments");
        console.error("Error deleting VTN document:", error);
      }
    }
  };

  const isImageFile = (file) => {
    const extension = file.name.split(".").pop().toLowerCase();
    return (
      SUPPORTED_IMAGE_EXTENSIONS.includes(extension) ||
      file.type.startsWith("image/")
    );
  };

  const getFilePreview = async (file) => {
    if (isImageFile(file)) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve({ type: "image", url: e.target.result });
        reader.readAsDataURL(file);
      });
    }

    if (
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel"
    ) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const workbook = XLSX.read(e.target.result, { type: "array" });
          const firstSheet = workbook.SheetNames[0];
          const preview = XLSX.utils
            .sheet_to_json(workbook.Sheets[firstSheet], { header: 1 })
            .slice(0, 5);
          resolve({ type: "excel", preview });
        };
        reader.readAsArrayBuffer(file);
      });
    }

    if (file.type === "application/pdf") {
      return { type: "pdf" };
    }

    if (
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      file.name.toLowerCase().endsWith(".docx")
    ) {
      return { type: "word" };
    }

    if (file.name.endsWith(".dwg") || file.name.endsWith(".dxf")) {
      return { type: "autocad" };
    }

    return { type: "file" };
  };

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    newExpanded.has(category)
      ? newExpanded.delete(category)
      : newExpanded.add(category);
    setExpandedCategories(newExpanded);
  };

  const handleLabelDragStart = (e, label) => {
    e.dataTransfer.setData("text/plain", label);
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleDocumentDrop = async (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("bg-blue-50");
    const files = Array.from(e.dataTransfer.files);

    for (const file of files) {
      const preview = await getFilePreview(file);
      setDocuments((prev) => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          name: file.name,
          file: file,
          fileType: file.type,
          preview,
          labels: [],
        },
      ]);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      const preview = await getFilePreview(file);
      setDocuments((prev) => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          name: file.name,
          file: file,
          fileType: file.type,
          preview,
          labels: [],
        },
      ]);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLabelDrop = (e, docId) => {
    e.preventDefault();
    e.currentTarget.classList.remove("bg-blue-50");
    const label = e.dataTransfer.getData("text/plain");

    setDocuments((docs) =>
      docs.map((doc) => {
        if (doc.id === docId && !doc.labels.includes(label)) {
          return { ...doc, labels: [...doc.labels, label] };
        }
        return doc;
      })
    );
  };

  // Enhanced document submission with automatic employee assignment
  const handleSubmit = async () => {
    setLoading(true);
    try {
      console.log(
        "🚀 Starting enhanced document upload with employee assignment..."
      );

      for (const doc of documents) {
        if (doc.labels.length > 0) {
          for (const label of doc.labels) {
            const formData = new FormData();
            formData.append("document", doc.file);

            const categoryId = categoryIds[label];

            if (!categoryId) {
              console.error(`No category ID found for label: ${label}`);
              continue;
            }

            console.log(
              `📄 Uploading document: ${doc.name} to category: ${label}`
            );

            // Step 1: Upload the document
            const uploadResponse = await fetch(
              `${base_url}/v1/upload/document?ANR=${anr}&categoryId=${categoryId}`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                body: formData,
              }
            );

            if (uploadResponse.ok) {
              const uploadResult = await uploadResponse.json();
              console.log("✅ Document uploaded successfully:", uploadResult);

              // Step 2: Enhanced category assignment with employee auto-assignment
              if (uploadResult.document && uploadResult.document._id) {
                try {
                  const enhancedAssignmentResponse = await fetch(
                    `${base_url}/v1/assignCategoryWithEmployees`,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify({
                        _id: uploadResult.document._id,
                        categoryId: categoryId,
                        autoAssignEmployees: true,
                        projectId: anr,
                        documentName: doc.name,
                      }),
                    }
                  );

                  if (enhancedAssignmentResponse.ok) {
                    const assignmentResult =
                      await enhancedAssignmentResponse.json();
                    console.log(
                      "👥 Enhanced category assignment completed:",
                      assignmentResult
                    );

                    if (
                      assignmentResult.categoryDetails &&
                      assignmentResult.categoryDetails.employeeCount > 0
                    ) {
                      setSuccess(
                        `Dokument erfolgreich hochgeladen und ${assignmentResult.categoryDetails.employeeCount} Mitarbeiter automatisch zugewiesen!`
                      );
                    } else {
                      setSuccess("Dokument erfolgreich hochgeladen!");
                    }
                  } else {
                    console.warn(
                      "⚠️ Enhanced assignment failed, but document uploaded successfully"
                    );
                    setSuccess(
                      "Dokument hochgeladen, aber Mitarbeiterzuweisung fehlgeschlagen."
                    );
                  }
                } catch (assignmentError) {
                  console.error(
                    "❌ Error in enhanced category assignment:",
                    assignmentError
                  );
                  setSuccess(
                    "Dokument hochgeladen, aber Mitarbeiterzuweisung fehlgeschlagen."
                  );
                }
              }
            } else {
              console.error(
                "❌ Document upload failed:",
                uploadResponse.statusText
              );
              setError(`Fehler beim Hochladen von ${doc.name}`);
            }
          }
        }
      }
      setDocuments([]);
      // Success message is now set individually for each document
      console.log("🎉 All document uploads completed!");
    } catch (error) {
      console.error("Upload error:", error);
      setError("Fehler beim Hochladen der Dokumente");
      console.error("❌ Upload process failed:", error);
    }
    setLoading(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("bg-blue-50");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("bg-blue-50");
  };

  const removeDocument = (docId) => {
    setDocuments((docs) => docs.filter((doc) => doc.id !== docId));
  };

  const removeLabel = (docId, labelToRemove) => {
    setDocuments((docs) =>
      docs.map((doc) => {
        if (doc.id === docId) {
          return {
            ...doc,
            labels: doc.labels.filter((label) => label !== labelToRemove),
          };
        }
        return doc;
      })
    );
  };

  const renderFilePreview = (doc) => {
    switch (doc.preview.type) {
      case "image":
        return (
          <img
            src={doc.preview.url}
            alt={doc.name}
            className="w-full h-48 object-cover rounded"
          />
        );
      case "excel":
        return (
          <div className="w-full h-48 overflow-auto bg-gray-50 p-2 rounded text-sm">
            <table className="w-full">
              <tbody>
                {doc.preview.preview.map((row, i) => (
                  <tr key={i} className="border-b">
                    {row.map((cell, j) => (
                      <td key={j} className="p-1">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "pdf":
        return (
          <div className="w-full h-48 flex items-center justify-center bg-gray-50 rounded">
            <FileText className="w-16 h-16 text-gray-400" />
          </div>
        );
      case "word":
        return (
          <div className="w-full h-48 flex items-center justify-center bg-gray-50 rounded">
            <FileEdit className="w-16 h-16 text-gray-400" />
            <span className="ml-2">Word Document</span>
          </div>
        );
      case "autocad":
        return (
          <div className="w-full h-48 flex items-center justify-center bg-gray-50 rounded">
            <File className="w-16 h-16 text-gray-400" />
            <span className="ml-2">AutoCAD File</span>
          </div>
        );
      default:
        return (
          <div className="w-full h-48 flex items-center justify-center bg-gray-50 rounded">
            <File className="w-16 h-16 text-gray-400" />
          </div>
        );
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
  };

  return (
    <div className="flex h-full bg-gray-100">
      {/* Categories Sidebar */}
      <Paper 
        elevation={1} 
        sx={{ 
          width: { xs: '100%', md: 256 },
          display: { xs: isSidebarOpen ? 'block' : 'none', md: 'block' },
          overflow: "auto", 
          p: 2,
          position: { xs: 'fixed', md: 'relative' },
          zIndex: { xs: 1200, md: 'auto' },
          height: { xs: '100vh', md: 'auto' },
          maxHeight: { xs: '100vh', md: 'none' }
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => setIsSidebarOpen(false)}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, p: 0.5 }}
            >
              <X size={20} />
            </IconButton>
            <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
              Dokumentkategorien
            </Typography>
          </Box>
          <Button
            onClick={() => setShowCreateModal(true)}
            color="primary"
            sx={{ minWidth: "auto", p: 1 }}
          >
            <Plus />
          </Button>
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* VTN Document Section */}
        <Paper elevation={2} sx={{ mb: 2, p: 2, backgroundColor: "#f8f9fa" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "#1976d2", fontSize: "0.9rem" }}
            >
              <VTNIcon
                size={16}
                style={{ marginRight: 8, verticalAlign: "middle" }}
              />
              VTN Dokumente
            </Typography>
            <Button
              onClick={() => {
                setEditingVTN(null);
                setShowVTNForm(true);
              }}
              variant="contained"
              size="small"
              startIcon={<Plus size={16} />}
              sx={{ minHeight: 32, fontSize: "0.8rem" }}
            >
              Neu
            </Button>
          </Box>

          {vtnDocuments.length > 0 ? (
            <Box sx={{ maxHeight: 200, overflow: "auto" }}>
              {vtnDocuments.map((vtn) => (
                <Card key={vtn._id} sx={{ mb: 1, elevation: 1 }}>
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", fontSize: "0.8rem" }}
                        >
                          {vtn.orderNumber || "VTN Dokument"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {vtn.date
                            ? new Date(vtn.date).toLocaleDateString("de-DE")
                            : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Bearbeiten">
                          <IconButton
                            size="small"
                            onClick={() => handleEditVTN(vtn)}
                            sx={{ color: "#1976d2" }}
                          >
                            <Edit3 size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteVTN(vtn._id)}
                            sx={{ color: "#d32f2f" }}
                          >
                            <X size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: "center", py: 2, fontSize: "0.8rem" }}
            >
              Keine VTN Dokumente vorhanden
            </Typography>
          )}
          {/* Favourites toggle */}
          <Box sx={{ display: "flex", gap: 1, mt: 2, mb: 1 }}>
            <Button
              variant={!showFavouritesOnly ? "contained" : "outlined"}
              size="small"
              onClick={() => setShowFavouritesOnly(false)}
            >
              Alle Labels
            </Button>
            <Button
              variant={showFavouritesOnly ? "contained" : "outlined"}
              size="small"
              onClick={() => setShowFavouritesOnly(true)}
            >
              Favoriten
            </Button>
          </Box>
        </Paper>
        {Object.entries(categories).map(([category, labels]) => {
          // If it's  Image category, directly show the labels
          if (category === "Profile Image") {
            return labels.map((label) => (
              <div
                key={label}
                className="p-2 hover:bg-blue-100 cursor-move rounded mb-2 flex items-center justify-between"
                draggable
                onDragStart={(e) => handleLabelDragStart(e, label)}
              >
                <div className="flex items-center flex-1">
                  <div
                    style={{
                      backgroundColor:
                        subcategories.find((sub) => sub.name === label)
                          ?.colorCode || "#1976d2",
                    }}
                    className="w-3 h-3 rounded-full mr-2"
                  />
                  <span>{label}</span>
                  {subcategories.find((sub) => sub.name === label)
                    ?.assignedEmployees?.length > 0 && (
                    <Chip
                      size="small"
                      style={{
                        marginLeft: 8,
                        backgroundColor:
                          (subcategories.find((sub) => sub.name === label)
                            ?.colorCode || "#1976d2") + "20",
                        color:
                          subcategories.find((sub) => sub.name === label)
                            ?.colorCode || "#1976d2",
                      }}
                    >
                      <Users className="w-3 h-3 mr-1" />
                      {
                        subcategories.find((sub) => sub.name === label)
                          ?.assignedEmployees?.length
                      }
                    </Chip>
                  )}
                </div>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip
                    title={
                      favouriteLabels.has(label)
                        ? "Entfernen aus Favoriten"
                        : "Zu Favoriten hinzufügen"
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await handleToggleFavourite(label);
                      }}
                      sx={{
                        color: favouriteLabels.has(label)
                          ? "#f59e0b"
                          : "inherit",
                      }}
                    >
                      {favouriteLabels.has(label) ? (
                        <Star size={14} />
                      ) : (
                        <StarOff size={14} />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kategorie bearbeiten">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleEnhanceSubcategory({
                          name: label,
                          _id: categoryIds[label],
                        })
                      }
                    >
                      <Settings className="w-4 h-4" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </div>
            ));
          }
          if (Array.isArray(labels) && labels.length > 0) {
            // For all other categories, show the expandable structure
            return (
              <div key={category} className="mb-2">
                <div
                  className="flex items-center cursor-pointer hover:bg-gray-100 p-2 rounded justify-between"
                  onClick={() => toggleCategory(category)}
                >
                  <div className="flex items-center">
                    {expandedCategories.has(category) ? (
                      <ChevronDown className="w-4 h-4 mr-2" />
                    ) : (
                      <ChevronRight className="w-4 h-4 mr-2" />
                    )}
                    <Typography>{category}</Typography>
                  </div>
                  <Tooltip title="Kategorie bearbeiten">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEnhanceSubcategory({
                          name: category,
                          _id: categoryIds[category],
                        });
                      }}
                    >
                      <Settings className="w-4 h-4" />
                    </IconButton>
                  </Tooltip>
                </div>

                {expandedCategories.has(category) && (
                  <div className="ml-4">
                    {labels
                      .filter(
                        (label) =>
                          !showFavouritesOnly || favouriteLabels.has(label)
                      )
                      .map((label) => (
                        <div
                          key={label}
                          className="p-2 hover:bg-blue-100 cursor-move rounded flex items-center justify-between"
                          draggable
                          onDragStart={(e) => handleLabelDragStart(e, label)}
                        >
                          <div className="flex items-center flex-1">
                            <div
                              style={{
                                backgroundColor:
                                  subcategories.find(
                                    (sub) => sub.name === label
                                  )?.colorCode || "#1976d2",
                              }}
                              className="w-3 h-3 rounded-full mr-2"
                            />
                            <span>{label}</span>
                            {subcategories.find((sub) => sub.name === label)
                              ?.assignedEmployees?.length > 0 && (
                              <Chip
                                size="small"
                                style={{
                                  marginLeft: 8,
                                  backgroundColor:
                                    (subcategories.find(
                                      (sub) => sub.name === label
                                    )?.colorCode || "#1976d2") + "20",
                                  color:
                                    subcategories.find(
                                      (sub) => sub.name === label
                                    )?.colorCode || "#1976d2",
                                }}
                              >
                                <Users className="w-3 h-3 mr-1" />
                                {
                                  subcategories.find(
                                    (sub) => sub.name === label
                                  )?.assignedEmployees?.length
                                }
                              </Chip>
                            )}
                          </div>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Tooltip
                              title={
                                favouriteLabels.has(label)
                                  ? "Entfernen aus Favoriten"
                                  : "Zu Favoriten hinzufügen"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await handleToggleFavourite(label);
                                }}
                                sx={{
                                  color: favouriteLabels.has(label)
                                    ? "#f59e0b"
                                    : "inherit",
                                }}
                              >
                                {favouriteLabels.has(label) ? (
                                  <Star size={14} />
                                ) : (
                                  <StarOff size={14} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Unterkategorie bearbeiten">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleEnhanceSubcategory({
                                    name: label,
                                    _id: categoryIds[label],
                                  })
                                }
                              >
                                <Edit3 className="w-4 h-4" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          } else {
            return (
              <div
                key={labels}
                className="p-2 hover:bg-blue-100 cursor-move rounded mb-2 flex items-center justify-between"
                draggable
                onDragStart={(e) => handleLabelDragStart(e, labels)}
              >
                <div className="flex items-center flex-1">
                  <div
                    style={{
                      backgroundColor:
                        subcategories.find((sub) => sub.name === labels)
                          ?.colorCode || "#1976d2",
                    }}
                    className="w-3 h-3 rounded-full mr-2"
                  />
                  <span>{labels}</span>
                  {subcategories.find((sub) => sub.name === labels)
                    ?.assignedEmployees?.length > 0 && (
                    <Chip
                      size="small"
                      style={{
                        marginLeft: 8,
                        backgroundColor:
                          (subcategories.find((sub) => sub.name === labels)
                            ?.colorCode || "#1976d2") + "20",
                        color:
                          subcategories.find((sub) => sub.name === labels)
                            ?.colorCode || "#1976d2",
                      }}
                    >
                      <Users className="w-3 h-3 mr-1" />
                      {
                        subcategories.find((sub) => sub.name === labels)
                          ?.assignedEmployees?.length
                      }
                    </Chip>
                  )}
                </div>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip
                    title={
                      favouriteLabels.has(labels)
                        ? "Entfernen aus Favoriten"
                        : "Zu Favoriten hinzufügen"
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await handleToggleFavourite(labels);
                      }}
                      sx={{
                        color: favouriteLabels.has(labels)
                          ? "#f59e0b"
                          : "inherit",
                      }}
                    >
                      {favouriteLabels.has(labels) ? (
                        <Star size={14} />
                      ) : (
                        <StarOff size={14} />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kategorie bearbeiten">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleEnhanceSubcategory({
                          name: labels,
                          _id: categoryIds[labels],
                        })
                      }
                    >
                      <Settings className="w-4 h-4" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </div>
            );
          }
        })}
      </Paper>

      {/* Create Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setError("");
          setSuccess("");
          setNewLabel({ name: "", personalCategory: false });
          setNewSubCategoryName("");
          setSelectedLabel("");
          setEnableEmployeeSelect(false);
        }}
      >
        <Box sx={{ ...modalStyle, maxWidth: 420, width: { xs: '90%', sm: '100%' } }}>
          {/* <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
            Create New Label/Subcategory
          </Typography> */}

          {/* Create Label */}
          {/* <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              New Label
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                value={newLabel.name}
                onChange={(e) => setNewLabel((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Enter label name"
                size="small"
                fullWidth
              />
              <Button
                onClick={handleCreateLabel}
                variant="contained"
                color="primary"
              >
                Create
              </Button>
            </Box>
          </Box> */}

          {/* Create Subcategory */}
          <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{ mb: 2, textAlign: "center" }}
            >
              New Category
            </Typography>
            <TextField
              value={newSubCategoryName}
              onChange={(e) => setNewSubCategoryName(e.target.value)}
              placeholder="Enter category name"
              size="small"
              fullWidth
              label="Category Name"
            />
            <RadioGroup
              row
              value={categoryType}
              onChange={(e) => setCategoryType(e.target.value)}
              sx={{ mb: 1 }}
            >
              <FormControlLabel
                value="personal"
                control={<Radio />}
                label="Personal Category"
              />
              <FormControlLabel
                value="employee"
                control={<Radio />}
                label="Select Employees"
              />
            </RadioGroup>
            <FormControl
              fullWidth
              size="small"
              disabled={categoryType !== "employee"}
            >
              <InputLabel>Assign Employees</InputLabel>
              <Select
                multiple
                value={selectedEmployees}
                onChange={(e) => {
                  const value = e.target.value;
                  // If 'all' is selected, select all employees
                  if (value.includes("all")) {
                    if (selectedEmployees.length === employees.length) {
                      setSelectedEmployees([]);
                    } else {
                      setSelectedEmployees(employees.map((emp) => emp._id));
                    }
                  } else {
                    setSelectedEmployees(value);
                  }
                }}
                label="Assign Employees"
                renderValue={(selected) =>
                  selected.length === employees.length
                    ? "All Employees"
                    : employees
                        .filter((emp) => selected.includes(emp._id))
                        .map((emp) => emp.name)
                        .join(", ")
                }
                sx={{ minHeight: 48, background: "#fafbfc", borderRadius: 1 }}
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={
                      selectedEmployees.length === employees.length &&
                      employees.length > 0
                    }
                    indeterminate={
                      selectedEmployees.length > 0 &&
                      selectedEmployees.length < employees.length
                    }
                  />
                  Select All Employees
                </MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp._id} value={emp._id}>
                    <Checkbox checked={selectedEmployees.includes(emp._id)} />
                    {emp.name} {emp.userType === "admin" ? "(Admin)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Color Palette Selection */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Farbe wählen
              </Typography>
              <Grid container spacing={1}>
                {colorPalette.map((color) => (
                  <Grid item key={color}>
                    <IconButton
                      onClick={() => setSubcategoryColor(color)}
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: color,
                        border:
                          subcategoryColor === color
                            ? "3px solid #000"
                            : "1px solid #ccc",
                        "&:hover": {
                          backgroundColor: color,
                          opacity: 0.8,
                        },
                      }}
                    >
                      {subcategoryColor === color && (
                        <Palette
                          sx={{ width: 16, height: 16, color: "#fff" }}
                        />
                      )}
                    </IconButton>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                onClick={() => {
                  setShowCreateModal(false);
                  setError("");
                  setSuccess("");
                  setNewLabel({ name: "", personalCategory: false });
                  setNewSubCategoryName("");
                  setSelectedLabel("");
                  setEnableEmployeeSelect(false);
                  setSelectedEmployees([]);
                  setSubcategoryColor("#1976d2");
                }}
                variant="outlined"
                fullWidth
              >
                Schließen
              </Button>
              <Button
                onClick={handleCreateSubCategory}
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<Save />}
              >
                Erstellen
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Enhanced Subcategory Management Modal */}
      <Modal
        open={showSubcategoryModal}
        onClose={() => {
          setShowSubcategoryModal(false);
          setSelectedSubcategory(null);
          setSubcategoryEmployees([]);
          setSubcategoryColor("#1976d2");
        }}
      >
        <Box sx={{ ...modalStyle, maxWidth: 500, width: { xs: '90%', sm: '100%' }, maxHeight: { xs: '90vh', sm: 'auto' }, overflow: 'auto' }}>
          <Typography
            variant="h6"
            component="h3"
            sx={{ mb: 3, textAlign: "center" }}
          >
            Kategorie bearbeiten: {selectedSubcategory?.name}
          </Typography>

          {/* Employee Assignment Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Mitarbeiter zuweisen
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Mitarbeiter auswählen</InputLabel>
              <Select
                multiple
                value={subcategoryEmployees}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.includes("all")) {
                    if (subcategoryEmployees.length === employees.length) {
                      setSubcategoryEmployees([]);
                    } else {
                      setSubcategoryEmployees(employees.map((emp) => emp._id));
                    }
                  } else {
                    setSubcategoryEmployees(value);
                  }
                }}
                label="Mitarbeiter auswählen"
                renderValue={(selected) => {
                  if (selected.length === 0)
                    return "Keine Mitarbeiter ausgewählt";
                  if (selected.length === employees.length)
                    return "Alle Mitarbeiter";
                  return employees
                    .filter((emp) => selected.includes(emp._id))
                    .map((emp) => emp.name)
                    .join(", ");
                }}
                sx={{ minHeight: 48, background: "#fafbfc", borderRadius: 1 }}
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={
                      subcategoryEmployees.length === employees.length &&
                      employees.length > 0
                    }
                    indeterminate={
                      subcategoryEmployees.length > 0 &&
                      subcategoryEmployees.length < employees.length
                    }
                  />
                  Alle Mitarbeiter auswählen
                </MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp._id} value={emp._id}>
                    <Checkbox
                      checked={subcategoryEmployees.includes(emp._id)}
                    />
                    <Avatar
                      sx={{ width: 24, height: 24, mr: 1, fontSize: "0.75rem" }}
                    >
                      {emp.name.charAt(0)}
                    </Avatar>
                    {emp.name} {emp.userType === "admin" ? "(Admin)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Selected Employees Display */}
            {subcategoryEmployees.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Ausgewählte Mitarbeiter:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {employees
                    .filter((emp) => subcategoryEmployees.includes(emp._id))
                    .map((emp) => (
                      <Chip
                        key={emp._id}
                        label={emp.name}
                        size="small"
                        avatar={
                          <Avatar
                            sx={{ width: 20, height: 20, fontSize: "0.7rem" }}
                          >
                            {emp.name.charAt(0)}
                          </Avatar>
                        }
                        onDelete={() => {
                          setSubcategoryEmployees((prev) =>
                            prev.filter((id) => id !== emp._id)
                          );
                        }}
                        sx={{
                          backgroundColor: subcategoryColor + "20",
                          color: subcategoryColor,
                        }}
                      />
                    ))}
                </Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Color Selection Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Kategoriefarbe
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: subcategoryColor,
                  borderRadius: 1,
                  border: "2px solid #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Palette sx={{ color: "#fff", width: 20, height: 20 }} />
              </Box>
              <Typography variant="body2" color="textSecondary">
                Ausgewählte Farbe: {subcategoryColor}
              </Typography>
            </Box>

            <Grid container spacing={1}>
              {colorPalette.map((color) => (
                <Grid item key={color}>
                  <IconButton
                    onClick={() => setSubcategoryColor(color)}
                    sx={{
                      width: 36,
                      height: 36,
                      backgroundColor: color,
                      border:
                        subcategoryColor === color
                          ? "3px solid #000"
                          : "1px solid #ccc",
                      "&:hover": {
                        backgroundColor: color,
                        opacity: 0.8,
                      },
                    }}
                  >
                    {subcategoryColor === color && (
                      <Badge
                        badgeContent="✓"
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: "#fff",
                            color: "#000",
                            fontSize: "0.7rem",
                            minWidth: 16,
                            height: 16,
                          },
                        }}
                      />
                    )}
                  </IconButton>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button
              onClick={() => {
                setShowSubcategoryModal(false);
                setSelectedSubcategory(null);
                setSubcategoryEmployees([]);
                setSubcategoryColor("#1976d2");
              }}
              variant="outlined"
              fullWidth
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSaveSubcategoryEnhancements}
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<Save />}
            >
              Speichern
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Document Upload Area */}
      <Box sx={{ 
        flex: 1, 
        p: { xs: 2, md: 3 },
        width: { xs: '100%', md: 'auto' },
        overflow: 'auto'
      }}>
        {/* Mobile Menu Toggle */}
        <IconButton
          onClick={() => setIsSidebarOpen(true)}
          sx={{ 
            display: { xs: isSidebarOpen ? 'none' : 'flex', md: 'none' },
            mb: 2,
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' }
          }}
        >
          <Settings size={20} />
        </IconButton>

        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 3, md: 6 },
            textAlign: "center",
            border: "2px dashed",
            borderColor: "grey.300",
            "&:hover": {
              borderColor: "primary.main",
            },
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDocumentDrop}
        >
          <Upload sx={{ width: { xs: 32, md: 48 }, height: { xs: 32, md: 48 }, color: "grey.400", mb: 2 }} />
          <Typography color="textSecondary" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
            {loading ? "Uploading..." : "Drag and drop files here"}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            Supports all image formats (JPG, PNG, GIF, etc.), Word documents,
            PDFs, Excel, AutoCAD files, and more
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            startIcon={<Upload />}
            sx={{ mt: 2 }}
          >
            Choose Files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf"
          />
        </Paper>

        {/* Submit Button */}
        {documents.length > 0 && (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mb: 3 }}
          >
            {loading ? "Uploading..." : "Upload Labeled Documents"}
          </Button>
        )}

        {/* Documents Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: { xs: 2, md: 3 },
          }}
        >
          {documents.map((doc) => (
            <Paper
              key={doc.id}
              sx={{ p: 2 }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleLabelDrop(e, doc.id)}
            >
              <Box sx={{ position: "relative" }}>
                {renderFilePreview(doc)}
                <Button
                  onClick={() => removeDocument(doc.id)}
                  color="error"
                  sx={{
                    minWidth: "auto",
                    p: 0.5,
                    position: "absolute",
                    top: 8,
                    right: 8,
                    borderRadius: "50%",
                  }}
                >
                  <X />
                </Button>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {doc.name}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {doc.labels.map((label) => (
                  <Box
                    key={label}
                    sx={{
                      bgcolor: "primary.50",
                      color: "primary.700",
                      px: 1,
                      py: 0.5,
                      borderRadius: "full",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "0.875rem",
                    }}
                  >
                    {label}
                    <Button
                      onClick={() => removeLabel(doc.id, label)}
                      sx={{ ml: 0.5, minWidth: "auto", p: 0 }}
                    >
                      <X sx={{ width: 16, height: 16 }} />
                    </Button>
                  </Box>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={Boolean(success || error)}
        autoHideDuration={6000}
        onClose={() => {
          setSuccess("");
          setError("");
        }}
      >
        <Alert
          severity={success ? "success" : "error"}
          onClose={() => {
            setSuccess("");
            setError("");
          }}
        >
          {success || error}
        </Alert>
      </Snackbar>

      {/* VTN Document Form Modal */}
      <VTNDocumentForm
        open={showVTNForm}
        onClose={() => {
          setShowVTNForm(false);
          setEditingVTN(null);
        }}
        vtnData={editingVTN}
        anr={anr}
        onSave={handleVTNSave}
      />
    </div>
  );
};

export default DocumentLabeler;
